// SPDX-License-Identifier: BUSL-1.1

/**
 * TenantRunnerPoolPage — VM pool monitoring + cost visibility.
 *
 * Path: /tenant-runner-pool
 * Auth: sessionAuth via RequireAuth in app.tsx
 *
 * Shows:
 * - Active runner VMs (server ID, status, age, job)
 * - Cost summary card with total estimated spend
 * - Real-time refresh (every 30s via React Query refetchInterval)
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { VmPoolTable } from '@/components/vm-pool-table';
import { RunnerClassTable } from '@/components/runner-class-table';
import { RunnerHealthPanel } from '@/components/runner-health-panel';
import { CostSummaryCard } from '@/components/cost-summary-card';
import { TenantSettingsSkeleton } from '@/components/tenant-settings-skeleton';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { Server, Activity } from 'lucide-react';

interface TenantSettings {
  provider: string | null;
  tokenMasked: string | null;
}

type TabKey = 'runners' | 'health';

const TABS: { key: TabKey; label: string; icon: typeof Server }[] = [
  { key: 'runners', label: 'Runners', icon: Server },
  { key: 'health', label: 'Runner Health', icon: Activity },
];

export function TenantRunnerPoolPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('runners');
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => apiFetch<TenantSettings>('/tenant/settings'),
  });

  if (settingsLoading) return <TenantSettingsSkeleton />;

  const hasCredentials = settings?.provider !== null;

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-100">Runner Pool</h1>
          <p className="mt-1 text-sm text-neutral-400">
            Active self-hosted runners provisioned in your Hetzner account.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <CostSummaryCard />
          <Link to="/tenant-settings">
            <Button variant="outline" size="sm">
              Configure
            </Button>
          </Link>
        </div>
      </div>

      {!hasCredentials ? (
        <Card className="flex flex-col items-center gap-4 p-12 text-center">
          <p className="text-sm text-neutral-400">
            No cloud provider configured yet.
          </p>
          <Link to="/tenant-settings">
            <Button variant="default" size="sm">
              Set up cloud provider
            </Button>
          </Link>
        </Card>
      ) : (
        <>
          {/* Tab navigation */}
          <div className="flex gap-1 rounded-lg border border-neutral-800 bg-neutral-900 p-1">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeTab === key
                    ? 'bg-neutral-800 text-white'
                    : 'text-neutral-400 hover:bg-neutral-800/50 hover:text-neutral-200'
                }`}
              >
                <Icon className="size-4" />
                {label}
              </button>
            ))}
          </div>

          {activeTab === 'runners' && (
            <>
              <Card className="p-4">
                <RunnerClassTable />
              </Card>

              <Card className="p-4">
                <VmPoolTable />
              </Card>

              <div className="flex flex-col gap-2 rounded border border-neutral-800 bg-neutral-900 p-4">
                <p className="text-xs text-neutral-500">
                  <strong className="text-neutral-400">Note:</strong> Cost estimates use Hetzner CX21
                  hourly rates (~$0.0075/hr). Actual billing from Hetzner may vary.
                  Costs are estimated from VM uptime — billed runners reflect actual Hetzner charges.
                </p>
                <p className="text-xs text-neutral-600">
                  VM state refreshes every 30 seconds. State is stored in Upstash Redis
                  and updated by GitHub App webhook events.
                </p>
              </div>
            </>
          )}

          {activeTab === 'health' && <RunnerHealthPanel />}
        </>
      )}
    </div>
  );
}